import { HttpClient, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, finalize, Subject, take, takeUntil } from 'rxjs';
import { NotificationService } from '../../core/core.module';
import { LgvLoanApplicationService } from '../../core/services/lgv-loan-application.service';
import { environment as env } from '../../../environments/environment';
import { model } from '@rxweb/reactive-form-validators';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { each } from 'jquery';

@Component({
    selector: 'lgv-loan-app-vault-modal',
    templateUrl: './loan-app-vault-modal.component.html',
    styleUrls: ['./loan-app-vault-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoanAppVaultModalComponent implements OnInit, OnDestroy {

    onDestroy$ = new Subject();
    isReady: boolean = false;
    loanAppId: string;
    clientId: string;
    documentId: string;
    mode: string;
    isReturnOnly = false;

    selectedFiles: File[] | null = null;
    selectedVaultDocument: LgvLoanApp.VaultDocument = null;

    isUploading$ = new BehaviorSubject<boolean>(false);
    uploadProgress$ = new BehaviorSubject<number>(0);

    _vaultForm: UntypedFormGroup;

    get readyToSubmit(): boolean {
        return (this.isAddMode && this._vaultForm.valid && !!this.selectedFiles) ||
            (this.isEditMode && this._vaultForm.valid) ||
            (this.isUploadMode && !!this.selectedFiles);
    }

    get isAddMode(): boolean { return this.mode === 'addVaultDocument'; }
    get isEditMode(): boolean { return this.mode === 'editVaultDocument'; }
    get isUploadMode(): boolean { return this.mode === 'uploadVaultDocument'; }

    @ViewChild('fileInput') fileInput!: ElementRef;

    constructor(
        private http: HttpClient,
        private fb: UntypedFormBuilder,
        public notificationSvc: NotificationService,
        public dialogRef: MatDialogRef<LoanAppVaultModalComponent>,
        private lgvLoanAppSvc: LgvLoanApplicationService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.clientId = data.clientId;
        this.loanAppId = data.loanAppId;
        this.mode = data.mode;
        this.isReturnOnly = data.isReturnOnly ?? false;
        this.selectedFiles = data.selectedFiles ?? null;
        this.selectedVaultDocument = data.vaultDocument ?? null;
    }

    ngOnInit(): void {
        if (this.isAddMode || this.isEditMode) {
            this.initVaultForm();
            this.isReady = true;
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    initVaultForm() {
        this._vaultForm = this.fb.group({
            id: [this.selectedVaultDocument?.documentId ?? null],
            loanApplicationId: [this.loanAppId],
            name: [this.selectedVaultDocument?.title ?? null, [Validators.required]],
            description: [this.selectedVaultDocument?.description ?? null],
            isInternal: [this.selectedVaultDocument?.isInternal ?? false]
        });
    }

    onSubmit() {
        if (this.isAddMode) {
            this.addVaultDocument();
        } else if (this.isEditMode) {
            this.updateVaultDocument();
        } else if (this.isUploadMode) {
            this.uploadVaultDocument();
        }
    }

    onCancel() {
        this.dialogRef.close({ action: false });
    }

    fileChangeEvent(event: any): void {
        if (event?.target?.files?.length > 0) {
            const files = event.target.files;
            console.log('files:');
            console.log(files);

            this.selectedFiles = files;
        }
    }

    addVaultDocument() {
        const formData = new FormData();
        for (let i = 0; i < this.selectedFiles.length; i++) {
            let selectedFile = this.selectedFiles[i];

            formData.append("document", selectedFile);
            formData.append("title", this._vaultForm.get('name').value);
            formData.append("description", this._vaultForm.get('description').value);
            formData.append("isInternal", this._vaultForm.get('isInternal').value ? 'true' : 'false');
            formData.append('ContentType', "form-data");

            this.isUploading$.next(true);
            let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${this.loanAppId}/vault/documents`;;

            const upload$ = this.http.post<any>(bldUrl, formData, {
                reportProgress: true,
                observe: 'events'
            })
                .pipe(finalize(() => {
                    console.log('FINALIZE');
                    //this.uploadSub = null;
                    this.isUploading$.next(false);
                }));

            upload$.subscribe(event => {
                if (event.type == HttpEventType.UploadProgress) {
                    this.uploadProgress$.next(Math.round(100 * (event.loaded / event.total)));
                    console.log(this.uploadProgress$.value);
                }
                if (event instanceof HttpResponse) {
                    if (event.statusText === "OK") {
                        this.notificationSvc.success('Vault document has been uploaded.');
                        this.dialogRef.close({ action: true });
                    }
                }
            },
                (err) => {
                    this.isUploading$.next(false);
                    console.log('*** Error uploading document. ***')
                    this.notificationSvc.error(`There was an error uploading your vault document.`)
                }
            );
        }
    }

    updateVaultDocument() {
        const vaultDoc = this._vaultForm.getRawValue();
        if (!this.documentId) return;
        this.lgvLoanAppSvc.updateVaultDocument(this.loanAppId, this.documentId, vaultDoc).pipe(takeUntil(this.onDestroy$)).subscribe(
            () => {
                this.notificationSvc.success('Vault document has been updated.');
                this.dialogRef.close({ action: true });
            },
            (err) => {
                console.error('Error updating vault document:', err);
                this.notificationSvc.error('There was an error updating the vault document.');
            }
        );
    }

    uploadVaultDocument() {
        // Implement logic if necessary
    }
}
